import { getRegisterUrlClientSide } from '@/app/utils/urlHelper';
import { useAuth0User } from '@/modules/Context/useAuth0User';
import { JobAlertPreferencesDialog } from '@/modules/JobAlerts';
import JobSearchFilters from '@/modules/JobSearchFilters';
import { DEFAULT_RADIUS } from '@/utils/jobSearchBoxOptions';
import cx from 'classnames';
import { PATH } from 'constants/path';
import Router, { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import {
  IJobDetail,
  IJobSpeciality,
  JobSearchWebCriteria,
  PaginationResult,
  URLQueryParamDict,
} from 'types/jobs';
import {
  convertJobSearchQueryToJobAlertQuery,
  generateJobListTrackingDetails,
} from 'utils/jobListingHelper';
import { scrollToResultsWhenRendered } from 'utils/jobSearchAutoScroll';
import { useAnalytics } from 'utils/useAnalytics';

import { useMediaQuery } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';

import JobListingJobCardList from 'modules/JobListingJobCardList';

import styles from './JobListing.module.scss';
import JobAlertsBanner from './components/JobAlertsBanner/JobAlertsBanner';
import NoResultsMessage from './components/NoResultsMessage/NoResultsMessage';
import ResultsHeader from './components/ResultsHeader/ResultsHeader';
import SimilarJobsSection from './components/SimilarJobsSection/SimilarJobsSection';
import { JobListingAlertBanner } from './utils/JobListingTypes';

const getJobListingBannerType = (query: JobSearchWebCriteria): JobListingAlertBanner => {
  const JOB_ALERT_CREATION_QUERY_KEYS = [
    'grade',
    'speciality',
    'rootSpeciality',
    'subspeciality',
    'workType',
    'startDate',
    'endDate',
    'keywords',
    'locationName',
    'location',
    'minPayRate',
    'minPayRateUnit',
  ];

  const isNonSchedulerLocationSelected = !!(query.lon && query.lat);
  const isPayRateRelatedSearch = !!(query.minPayRate && query.minPayRateUnit);
  const isMedicineOnlySearch = query.rootSpeciality === 'Medicine' && !query.subspeciality;
  const isAbleToCreateJobAlert = Boolean(JOB_ALERT_CREATION_QUERY_KEYS.find(key => query[key]));

  if (query.jobAlert === 'Manage') {
    return 'manage-job-alert';
  }

  if (isMedicineOnlySearch) {
    return 'select-subspeciality';
  }

  if (isAbleToCreateJobAlert) {
    if (isNonSchedulerLocationSelected || isPayRateRelatedSearch) {
      return 'none';
    } else {
      return 'job-alert-create';
    }
  } else {
    return 'select-search-criteria';
  }
};

interface JobListingProps {
  jobs: Omit<PaginationResult, 'page'>;
  similarJobs?: IJobDetail[];
  jobSpecialities: IJobSpeciality[];
  query: JobSearchWebCriteria;
}

const JobListing = ({
  jobs: { total, data, limit },
  similarJobs = [],
  query,
  jobSpecialities,
}: JobListingProps) => {
  const router = useRouter();
  const track = useAnalytics();
  const { user } = useAuth0User();

  const scrollToResultsRef = useRef<null | HTMLDivElement>(null);

  const [showDialog, setShowDialog] = useState(false);

  const trackingDetails = generateJobListTrackingDetails(query, user);

  const mobile = useMediaQuery(`(max-width: 600px)`);

  const radiusInKm = query.radius || DEFAULT_RADIUS;

  const hasSearchResults = !!data?.length;
  const hasSimilarResults = !!similarJobs?.length;
  const isNonSchedulerLocationSelected = !!(query.lon && query.lat);

  const [jobListingAlertBanner, setJobListingAlertBanner] = useState<JobListingAlertBanner>(
    getJobListingBannerType(query),
  );

  const jobSpecialitiesMapped = {};
  jobSpecialities.forEach(js => {
    jobSpecialitiesMapped[js.name] = js;
  });

  const sortByClick = type => {
    let search: URLQueryParamDict = {};
    search = Object.assign(search, query, { sortBy: type });
    if (!type) {
      delete search.sortBy;
    }
    router.push({ pathname: PATH.JOBSLIST, query: search });
  };

  const handleJobAlertBannerSaveCLick = () => {
    track('Set up job alert button click', trackingDetails);
    if (!user) {
      // noinspection JSIgnoredPromiseFromCall

      Router.push(getRegisterUrlClientSide('create_job_alert'));
    } else {
      setShowDialog(true);
    }
  };

  const handleJobAlertManageButtonClick = () => {
    track('Manage job alerts button click', trackingDetails);
    router.push(PATH.JOBS_ALERTS);
  };

  const onJobAlertSave = () => {
    setJobListingAlertBanner('manage-job-alert');
  };

  useEffect(() => {
    setJobListingAlertBanner(getJobListingBannerType(query));
  }, [router.query]);

  useEffect(() => {
    scrollToResultsWhenRendered(scrollToResultsRef);
  }, [router.query]);

  useEffect(() => {
    track('Job Search Results', {
      ...query,
      count: total,
      regCTAJobResultsView: user === null,
    });
  }, [router.query]);

  return (
    <div data-testid="job-listing-module">
      <p className={styles.pageHeadingCentered}>Search results</p>

      <div className={styles.container}>
        <div>
          <div className={styles.alertContainer} ref={scrollToResultsRef}>
            <JobAlertsBanner
              onSave={handleJobAlertBannerSaveCLick}
              onManage={handleJobAlertManageButtonClick}
              jobListingAlertBanner={jobListingAlertBanner}
            />
          </div>

          {!hasSearchResults && <NoResultsMessage />}

          {hasSearchResults && (
            <>
              <ResultsHeader
                jobSpecialitiesMapped={jobSpecialitiesMapped}
                query={query}
                total={total}
                isNonSchedulerLocationSelected={isNonSchedulerLocationSelected}
                mobile={mobile}
                radiusInKm={radiusInKm}
                sortByClick={sortByClick}
                trackingDetails={trackingDetails}
              />

              <div className={styles.jobCard} data-testid="job-search-jobs-listing-results">
                <JobListingJobCardList
                  jobs={data}
                  isUserLoggedIn={!!user}
                  query={query}
                  suppressCTABanner={hasSimilarResults}
                />
              </div>
            </>
          )}

          {hasSimilarResults && (
            <SimilarJobsSection
              className={cx({ [styles.noResultsSimilarJobs]: !hasSearchResults })}
              // we should always have grade in this case
              grade={query.grade ?? ''}
              speciality={query.speciality}
              rootSpeciality={query.rootSpeciality}
              subspeciality={query.subspeciality}
              hasSearchResults={hasSearchResults}
              jobs={similarJobs}
              isUserLoggedIn={!!user}
              query={query}
            />
          )}

          {hasSearchResults && total > limit && (
            <div className={styles.pagination}>
              <Pagination
                count={Math.ceil(total / limit)}
                page={(query.page && +query.page) || 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={(_e, value) => {
                  track('JobsList pagination', { 'Page number': value });

                  if (query['listing']) {
                    delete query['listing'];
                  }

                  let newQuery: URLQueryParamDict = {};
                  newQuery = Object.assign(newQuery, query);
                  newQuery.page = value;
                  router.push({ pathname: location.pathname, query: newQuery });
                }}
              />
            </div>
          )}
        </div>

        <div className={styles.filters}>
          <JobSearchFilters query={query} />
        </div>
      </div>

      <JobAlertPreferencesDialog
        query={convertJobSearchQueryToJobAlertQuery(query)}
        show={showDialog}
        hide={() => {
          setShowDialog(false);
        }}
        successCallBack={onJobAlertSave}
      />
    </div>
  );
};

export default JobListing;
